/**
 * Alliance Bear Planner (spec sections 30-34, 61).
 *
 * The unit of planning is a DAY, not a trap. The alliance runs two traps on the
 * same date, and taking part in one starts a ~48h participation cooldown
 * (`strategy_rules BEAR_TRAP/PARTICIPATION_COOLDOWN`), so a member is in BT1,
 * or BT2, or neither. That makes the assignment one exclusive choice per member
 * rather than two independent rosters, and the screen is shaped to match:
 *
 *   pick the day  ->  assign the alliance across both traps  ->  SAVE
 *                 ->  per trap: accept the recommended size or set your own
 *                 ->  per trap: generate the plan
 *
 * The save is the point. Before this, participant selection lived in React
 * state and was gone on reload, and the "no save button" that reported was the
 * visible half of a deeper problem: the plan was built from a roster nobody had
 * recorded, so nothing downstream could refer to it.
 *
 * The other invariants this screen carries are unchanged. Manual decisions win
 * — a per-day lead lock travels to the planner as a constraint (spec 51).
 * Assumptions are shown next to the plan, never buried. And a lead ranking row
 * carries its sample count, because an 87 built from two rallies is not an 87.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BearDaySummary, BearTrapNumber } from '@kso/schemas';
import { useAllianceMembers, useBearDay, useBearDays, useCreateBearDay } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import { Badge, Card, PageHeader, Value } from '../../components/ui/primitives';
import { EmptyState, ErrorState, LoadingBlock, QueryBoundary } from '../../components/ui/states';
import { useT } from '../../i18n';
import { TrapAssignmentPicker } from './TrapAssignmentPicker';
import { TrapPlanPanel } from './TrapPlanPanel';

const TRAPS: BearTrapNumber[] = [1, 2];

/** Today in the browser's own zone. The day is a calendar date, not an instant. */
function todayLocal(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function BearPlannerPage() {
  const t = useT();
  const core = useCoreContext();
  const members = useAllianceMembers(core.allianceId ?? undefined);

  const [eventId, setEventId] = useState<string | null>(null);
  const day = useBearDay(eventId ?? undefined);

  const memberById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members.data?.members ?? []) {
      map.set(member.id, member.name ?? member.id);
    }
    return map;
  }, [members.data]);

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (!core.allianceId) {
    return (
      <>
        <PageHeader title={t.planner.title} subtitle={t.bearDay.subtitle} />
        <EmptyState
          title={t.common.noCoreAllianceTitle}
          body={t.planner.noCoreAllianceBody}
          action={
            <Link className="btn btn--sm" to="/data/sync">
              {t.common.openSyncStatus}
            </Link>
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t.planner.title} subtitle={t.bearDay.subtitle} />

      <div className="stack">
        <DaySelector
          allianceId={core.allianceId}
          selected={day.data?.day ?? null}
          onSelect={setEventId}
        />

        {eventId ? (
          day.isPending ? (
            <LoadingBlock label={t.bearDay.loadingDay} rows={3} />
          ) : day.error ? (
            <ErrorState error={day.error} />
          ) : day.data ? (
            <>
              <QueryBoundary
                query={members}
                loadingLabel={t.common.loadingRoster}
                isEmpty={(data) => data.members.length === 0}
                empty={<EmptyState title={t.planner.noRosterTitle} body={t.planner.noRosterBody} />}
              >
                {(data) => (
                  <TrapAssignmentPicker
                    eventId={eventId}
                    members={data.members}
                    assignments={day.data.assignments}
                  />
                )}
              </QueryBoundary>

              {TRAPS.map((trapNumber) => {
                const trap = day.data.day.traps.find((candidate) => candidate.trapNumber === trapNumber);
                const assigned = day.data.assignments.filter((a) => a.trapNumber === trapNumber);
                return (
                  <TrapPlanPanel
                    key={trapNumber}
                    eventId={eventId}
                    trapNumber={trapNumber}
                    assignedCount={trap?.assignedCount ?? assigned.length}
                    assignedMembers={assigned.map((a) => ({
                      playerId: a.playerId,
                      playerName: a.playerName,
                    }))}
                    memberById={memberById}
                  />
                );
              })}
            </>
          ) : null
        ) : null}
      </div>
    </>
  );
}

/**
 * Choosing which Bear Trap day to work on, or opening a new one.
 *
 * Creating is idempotent on the server, so pressing it for a date that already
 * has a day lands on that day rather than failing — and it cannot produce a
 * second day for one date, which is what the whole one-trap-per-member
 * guarantee rests on.
 */
function DaySelector({
  allianceId,
  selected,
  onSelect,
}: {
  allianceId: string;
  selected: BearDaySummary | null;
  onSelect: (eventId: string | null) => void;
}) {
  const t = useT();
  const days = useBearDays(allianceId);
  const createDay = useCreateBearDay();
  const [date, setDate] = useState(todayLocal());

  if (selected) {
    return (
      <Card title={t.bearDay.dayTitle}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Badge tone="accent">{selected.date ?? t.common.unknown}</Badge>
          {selected.traps.map((trap) => (
            <span key={trap.trapNumber} className="muted small">
              {t.bearDay.trapAssignedCount(trap.trapNumber, trap.assignedCount)}
            </span>
          ))}
          <button type="button" className="btn btn--sm btn--subtle" onClick={() => onSelect(null)}>
            {t.planner.change}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t.bearDay.dayTitle}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.bearDay.dayBlurb}
      </p>

      <div className="row" style={{ gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted small">{t.bearDay.date}</span>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            aria-label={t.bearDay.date}
          />
        </label>

        <button
          type="button"
          className="btn"
          disabled={createDay.isPending || date.trim().length === 0}
          onClick={() =>
            createDay.mutate(
              { allianceId, date },
              { onSuccess: (created) => onSelect(created.eventId) },
            )
          }
        >
          {createDay.isPending ? t.planner.creating : t.bearDay.openDay}
        </button>
      </div>

      {createDay.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={createDay.error} />
        </div>
      ) : null}

      <div style={{ marginTop: 16 }}>
        <div className="muted small" style={{ marginBottom: 6 }}>
          {t.bearDay.recentDays}
        </div>
        <QueryBoundary
          query={days}
          loadingLabel={t.bearDay.loadingDays}
          isEmpty={(data) => data.days.length === 0}
          empty={
            <p className="muted small" style={{ margin: 0 }}>
              {t.bearDay.noDaysYet}
            </p>
          }
        >
          {(data) => (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t.bearDay.date}</th>
                    <th className="num">BT1</th>
                    <th className="num">BT2</th>
                    <th className="num">{t.bearDay.ralliesEntered}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.days.slice(0, 10).map((entry) => {
                    const rallies = entry.traps.reduce((sum, trap) => sum + trap.rallyCount, 0);
                    return (
                      <tr key={entry.eventId}>
                        <td className="nowrap">
                          <Value absent={t.common.notRecorded}>{entry.date}</Value>
                        </td>
                        {entry.traps.map((trap) => (
                          <td key={trap.trapNumber} className="num">
                            {trap.assignedCount}
                          </td>
                        ))}
                        <td className="num">{rallies}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--sm"
                            onClick={() => onSelect(entry.eventId)}
                          >
                            {t.planner.useIt}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </QueryBoundary>
      </div>
    </Card>
  );
}
